import React, { useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import html2canvas from 'html2canvas';

const ExportContainer = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto;
  font-family: 'IBM Plex Sans JP', sans-serif;
  color: #2D3653;
  display: none; /* Caché par défaut, affiché seulement pour l'export */
`;

const ExportTitle = styled.h1`
  color: #2D3653;
  text-align: center;
  font-family: 'Style Script', cursive;
  font-size: 28px;
  margin-bottom: 25px;
  margin-top: 0;
  position: relative;
  
  &::before, &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: #2D3653;
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
`;

const ExportTimeTable = styled.div`
  display: grid;
  grid-template-columns: 4fr 2fr 2fr;
  gap: 15px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(45, 54, 83, 0.3);
  
  &:last-child {
    border-bottom: none;
  }
`;

const ExportTimeLabel = styled.div`
  color: #2D3653;
  font-family: 'IBM Plex Sans JP', sans-serif;
  font-size: 18px;
  font-weight: 500;
`;

const ExportTimeValue = styled.div`
  color: #2D3653;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  text-align: right;
  font-size: 18px;
  font-weight: 600;
`;

const SectionExportImage = ({ sectionName, scheduleItems, onImageGenerated }) => {
  const exportRef = useRef(null);
  const isGenerating = useRef(false);

  // Fonction pour générer l'image
  const generateImage = useCallback(async () => {
    if (!exportRef.current || isGenerating.current) return;

    isGenerating.current = true;

    try {
      // Afficher temporairement le conteneur
      exportRef.current.style.display = 'block';
      
      // Attendre que le DOM soit mis à jour
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Haute résolution
        width: 600,
        height: exportRef.current.scrollHeight,
        useCORS: true,
        allowTaint: true
      });

      // Cacher à nouveau le conteneur
      exportRef.current.style.display = 'none';

      // Convertir en blob
      canvas.toBlob((blob) => {
        if (blob && onImageGenerated) {
          onImageGenerated(blob);
        }
        isGenerating.current = false;
      }, 'image/png', 0.9);

    } catch (error) {
      console.error('Erreur lors de la génération de l\'image:', error);
      // S'assurer que le conteneur est caché en cas d'erreur
      if (exportRef.current) {
        exportRef.current.style.display = 'none';
      }
      isGenerating.current = false;
    }
  }, [onImageGenerated]);

  // Générer l'image quand le composant est monté
  useEffect(() => {
    if (scheduleItems && scheduleItems.length > 0 && !isGenerating.current) {
      generateImage();
    }
  }, [scheduleItems, sectionName]);

  return (
    <ExportContainer ref={exportRef}>
      <ExportTitle>{sectionName}</ExportTitle>
      {scheduleItems && scheduleItems.map((item, index) => (
        <ExportTimeTable key={index}>
          <ExportTimeLabel>{item.name}</ExportTimeLabel>
          <ExportTimeLabel>{item.roomName}</ExportTimeLabel>
          <ExportTimeValue>{item.time}</ExportTimeValue>
        </ExportTimeTable>
      ))}
    </ExportContainer>
  );
};

export default SectionExportImage; 